import { Link } from 'react-router-dom';
import type { UserRole } from '@emazy/shared-types';

import { Container, PageHeader, Section } from '@/components/common';
import { routes } from '@/config/routes';
import { useAuth } from '@/features/auth/context';
import { roleLabels } from '@/features/auth/utils';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

function RoleBadges({ roles }: { roles: UserRole[] }) {
  return (
    <div className="role-badges" aria-label="Account roles">
      {roles.map((role) => (
        <span key={role}>{roleLabels[role]}</span>
      ))}
    </div>
  );
}

export function DashboardPage() {
  useDocumentMetadata('Dashboard');
  const { user } = useAuth();
  return (
    <>
      <PageHeader
        eyebrow="Your Emazy account"
        title={`Welcome, ${user?.firstName ?? 'friend'}`}
        description="This account shell verifies authentication and prepares for customer capabilities in M2C and M8."
        actions={user && <RoleBadges roles={user.roles} />}
      />
      <Section spacing="large">
        <Container>
          <div className="account-placeholder-grid">
            {[
              [
                'pi pi-images',
                'Saved designs',
                'Design saving arrives with customer account capabilities.',
              ],
              [
                'pi pi-receipt',
                'Recent orders',
                'Order data arrives after commerce and API milestones.',
              ],
              [
                'pi pi-refresh',
                'Reorders',
                'Repeat ordering from saved identifiers is planned for M8.',
              ],
            ].map(([icon, title, copy]) => (
              <article key={title}>
                <i className={icon} />
                <h2>{title}</h2>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
}

export function OrdersPage() {
  useDocumentMetadata('Orders');
  const { user } = useAuth();
  return (
    <>
      <PageHeader
        eyebrow="Account order history"
        title="Your orders"
        description={`Signed in as ${user?.email ?? ''}. Real order history is not connected yet.`}
      />
      <Section spacing="large">
        <Container size="narrow">
          <div className="state-card">
            <i className="pi pi-receipt" />
            <h2>No connected orders yet</h2>
            <p>Order history and tracking will appear after checkout and backend milestones.</p>
            <Link className="button-link button-link--primary" to={routes.products}>
              Browse products
            </Link>
          </div>
        </Container>
      </Section>
    </>
  );
}

function PortalPage({ kind }: { kind: 'admin' | 'workshop' }) {
  const admin = kind === 'admin';
  useDocumentMetadata(admin ? 'Admin Portal' : 'Workshop Portal');
  const { user } = useAuth();
  const sections = admin
    ? ['Products', 'Orders', 'Customers', 'Production partners']
    : ['Production queue', 'Production files', 'Shipment status', 'Barcode workflows'];
  return (
    <>
      <PageHeader
        eyebrow={admin ? 'Authorized administration access' : 'Authorized production access'}
        title={admin ? 'Admin portal foundation' : 'Workshop portal foundation'}
        description={`${user?.displayName ?? 'This account'} has access. Operational functionality arrives in a later milestone.`}
        actions={user && <RoleBadges roles={user.roles} />}
      />
      <Section spacing="large">
        <Container>
          <div className="account-placeholder-grid">
            {sections.map((title) => (
              <article key={title}>
                <i className="pi pi-lock-open" />
                <h2>{title}</h2>
                <p>Access confirmed; tools are not implemented in M2A.</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
}

export function AdminPage() {
  return <PortalPage kind="admin" />;
}
export function WorkshopPage() {
  return <PortalPage kind="workshop" />;
}
